import React from "react";
import styled from "styled-components";
import { State } from "../../types";
import {
  IconButton,
  BookingSummary,
  Logo,
  Description,
  Title,
} from "../../components";
import DateHelper from "../../helpers/DateHelper";
import { defaultThemeStyles } from "../../mocks";
import { ArrowLeftSolid } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import { CreateBookingStatus } from "../../domain_objects/BookingResponse";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

export type ErrorPageProps = {
  state: State;
  prevPage: (payload: Partial<State>) => void;
};

export const ErrorPage: React.FC<ErrorPageProps> = ({ state, prevPage }) => {
  const { bookingDetails, crossSellSetup, bookingConfirmationDetails } = state;
  const { t } = useTranslation();

  const displayedErrorText = () => {
    switch (bookingConfirmationDetails?.Status) {
      case CreateBookingStatus.CustomerBlocked:
        return t("ErrorCreatingBookingCustomerBlockedText");
      case CreateBookingStatus.NoAvailability:
        return t("EnquiryErrorNoAvailability");
      case CreateBookingStatus.InvalidPromotion:
        return t("ErrorCreatingBookingInvalidPromotionText");
      case CreateBookingStatus.InvalidBooking:
        return t("ErrorCreatingBookingText");
      case CreateBookingStatus.LeaveTimeConfirmationRequired:
        return t("EnquiryErrorNoAvailability");
      case CreateBookingStatus.AvailabilitySearchError:
        return t("ErrorAvailabilitySearch");
      case CreateBookingStatus.OutsideEnquiryPartySizeLimits:
        return t("EnquiryOutOfLimitsError");
      default:
        return t("EnquiryGenericError"); // Generic cover cases where status !== Success
    }
  };

  const backButtonClick = () => {
    prevPage(state);
  };

  return (
    <StyledPageContainer>
      <Logo styles={crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      <StyledHeader>
        <IconButton
          styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
          icon={ArrowLeftSolid}
          ariaLabel="Previous"
          onClick={backButtonClick}
          dataId="back-button"
        />
      </StyledHeader>
      <StyledTitle
        styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
      >
        {t("Error")}
      </StyledTitle>
      <Description styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}>
        {displayedErrorText()}
      </Description>
      <BookingSummary
        flashBookingSummaryPanel={state.flashBookingSummaryPanel || false}
        date={bookingDetails.selectedDate}
        numberOfCovers={bookingDetails.partySize}
        timeslot={
          bookingDetails.timeSlot
            ? DateHelper.sliceTimeSlot(bookingDetails.timeSlot)
            : undefined
        }
        styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
      />
    </StyledPageContainer>
  );
};

export default ErrorPage;
